import { useEffect, useRef } from "react"
import { useDisableNavigationOnSlide } from "./DisableNavigationOnSlide"

const diffForSwipe = 50

function isSwipe(current: number, previous: number): boolean {
	return Math.abs(current - previous) >= diffForSwipe
}
/**
 * Listen to Left-Right swipe movements
 * -1 => Go to prev (left to right swipe)
 * 1 => Go to next (right to left swipe)
 */
export function useSwipe<T extends HTMLElement>(
	onSwipe: (direction: 1 | -1) => void
) {
	const startX = useRef<number>(-1)
	const startY = useRef<number>(-1)
	// lets use the ref returned from disable navigation on slide
	const [ref, setRef] = useDisableNavigationOnSlide<T>()
	useEffect(() => {
		if (ref) {
			const prev = ref.style.userSelect
			ref.style.setProperty("user-select", "none")
			return () => {
				if (ref) {
					ref.style.setProperty("user-select", prev)
				}
			}
		}
		return
	}, [ref])
	useEffect(() => {
		function setStart({ x, y }: { x: number; y: number }) {
			startX.current = x
			startY.current = y
		}
		function handleEnd({ x, y }: { x?: number; y?: number }) {
			if (x !== undefined && isSwipe(x, startX.current)) {
				const deltaX = Math.abs(x - startX.current)
				const deltaY =
					y !== undefined && startY.current !== -1
						? Math.abs(y - startY.current)
						: 0
				// Only trigger swipe if horizontal movement is dominant over vertical scroll

				if (deltaX > deltaY) {
					if (x > startX.current) {
						// left -> right slide
						// want to go to prev/left- slide
						onSwipe(-1)
					} else if (x < startX.current) {
						// right -> left slide
						// want to go to next/right- slide
						onSwipe(1)
					}
				}
			}
		}
		function onTouchStart(event: TouchEvent) {
			const touch = event.touches.item(0)
			if (touch) {
				setStart({ x: touch.clientX, y: touch.clientY })
			}
		}
		function onMouseStart(event: MouseEvent) {
			setStart({ x: event.clientX, y: event.clientY })
		}
		function onTouchEnd(event: TouchEvent) {
			const touch = event.changedTouches.item(0)
			handleEnd({ x: touch?.clientX, y: touch?.clientY })
		}
		function onMouseUp(event: MouseEvent) {
			handleEnd({ x: event.clientX, y: event.clientY })
		}
		if (ref) {
			ref.addEventListener("touchstart", onTouchStart, { passive: true })
			ref.addEventListener("touchend", onTouchEnd, { passive: true })
			ref.addEventListener("mousedown", onMouseStart, { passive: true })
			ref.addEventListener("mouseup", onMouseUp, { passive: true })
		}
		return () => {
			if (ref) {
				ref.removeEventListener("touchstart", onTouchStart)
				ref.removeEventListener("mousedown", onMouseStart)
				ref.removeEventListener("touchend", onTouchEnd)
				ref.removeEventListener("mouseup", onMouseUp)
			}
		}
	}, [ref, onSwipe])
	return [setRef]
}
